import type { ApiClient } from "@carepass/api-client";
import type { Schemas } from "../workspace/api.generated";
import type { NotificationSearchValues } from "./schema";

export interface NotificationListQuery extends NotificationSearchValues {
  readonly page: number;
  readonly size: number;
}

export async function getNotifications(api: ApiClient, query: NotificationListQuery, signal?: AbortSignal) {
  const response = await api.get<Schemas["AdminPageNotificationAdminSummaryView"]>("/api/v1/admin/notifications", {
    query: {
      deliveryStatus: query.deliveryStatus || undefined,
      memberId: query.memberId ? Number(query.memberId) : undefined,
      campaignId: query.campaignId ? Number(query.campaignId) : undefined,
      from: query.from || undefined,
      to: query.to || undefined,
      page: query.page,
      size: query.size,
    },
    signal,
    cache: "no-store",
  });
  return response.data;
}

export async function getNotificationDetail(api: ApiClient, id: number, signal?: AbortSignal) {
  return (await api.get<Schemas["NotificationAdminDetailView"]>(`/api/v1/admin/notifications/${id}`, { signal, cache: "no-store" })).data;
}

export async function getNotificationTemplates(api: ApiClient, signal?: AbortSignal) {
  return (await api.get<Schemas["NotificationTemplateView"][]>("/api/v1/admin/notifications/templates", { signal, cache: "no-store" })).data;
}

export async function getRegisteredNotificationTemplates(api: ApiClient, page: number, keyword: string, signal?: AbortSignal) {
  return (await api.get<Schemas["RegisteredNotificationTemplatePage"]>("/api/v1/admin/notifications/registered-templates", { query: { page, keyword: keyword || undefined }, signal, cache: "no-store" })).data;
}

export async function getRegisteredNotificationTemplate(api: ApiClient, templateCode: string, senderKeyType: string, signal?: AbortSignal) {
  return (await api.get<Schemas["RegisteredNotificationTemplateDetail"]>(`/api/v1/admin/notifications/registered-templates/${encodeURIComponent(templateCode)}`, { query: { senderKeyType }, signal, cache: "no-store" })).data;
}

export async function getNotificationPolicies(api: ApiClient, signal?: AbortSignal) {
  return (await api.get<Schemas["NotificationPolicyView"][]>("/api/v1/admin/notifications/policies", { signal, cache: "no-store" })).data;
}

export async function getNotificationCampaigns(api: ApiClient, signal?: AbortSignal) {
  return (await api.get<Schemas["NotificationCampaignView"][]>("/api/v1/admin/notifications/campaigns", { signal, cache: "no-store" })).data;
}

export async function createSingleNotification(api: ApiClient, body: Schemas["CreateSingleNotificationRequest"], signal?: AbortSignal) {
  return (await api.post<Schemas["NotificationView"], Schemas["CreateSingleNotificationRequest"]>("/api/v1/admin/notifications", body, { signal, cache: "no-store" })).data;
}

export async function retryNotification(api: ApiClient, id: number, signal?: AbortSignal) {
  return (await api.post<Schemas["NotificationView"]>(`/api/v1/admin/notifications/${id}/retry`, undefined, { signal, cache: "no-store" })).data;
}

export async function createNotificationTemplate(api: ApiClient, body: Schemas["CreateNotificationTemplateRequest"], signal?: AbortSignal) {
  return (await api.post<Schemas["NotificationTemplateView"], Schemas["CreateNotificationTemplateRequest"]>("/api/v1/admin/notifications/templates", body, { signal, cache: "no-store" })).data;
}

export async function updateNotificationTemplate(api: ApiClient, id: number, body: Schemas["CreateNotificationTemplateRequest"], signal?: AbortSignal) {
  return (await api.put<Schemas["NotificationTemplateView"], Schemas["CreateNotificationTemplateRequest"]>(`/api/v1/admin/notifications/templates/${id}`, body, { signal, cache: "no-store" })).data;
}

export async function setNotificationTemplateActive(api: ApiClient, id: number, active: boolean, signal?: AbortSignal) {
  return (await api.post<Schemas["NotificationTemplateView"], Schemas["ChangeNotificationActiveRequest"]>(`/api/v1/admin/notifications/templates/${id}/active`, { active }, { signal, cache: "no-store" })).data;
}

export async function createNotificationPolicy(api: ApiClient, body: Schemas["CreateNotificationPolicyRequest"], signal?: AbortSignal) {
  return (await api.post<Schemas["NotificationPolicyView"], Schemas["CreateNotificationPolicyRequest"]>("/api/v1/admin/notifications/policies", body, { signal, cache: "no-store" })).data;
}

export async function updateNotificationPolicy(api: ApiClient, id: number, body: Schemas["CreateNotificationPolicyRequest"], signal?: AbortSignal) {
  return (await api.put<Schemas["NotificationPolicyView"], Schemas["CreateNotificationPolicyRequest"]>(`/api/v1/admin/notifications/policies/${id}`, body, { signal, cache: "no-store" })).data;
}

export async function setNotificationPolicyActive(api: ApiClient, id: number, active: boolean, signal?: AbortSignal) {
  return (await api.post<Schemas["NotificationPolicyView"], Schemas["ChangeNotificationActiveRequest"]>(`/api/v1/admin/notifications/policies/${id}/active`, { active }, { signal, cache: "no-store" })).data;
}

export async function createNotificationCampaign(api: ApiClient, body: Schemas["CreateNotificationCampaignRequest"], signal?: AbortSignal) {
  return (await api.post<Schemas["NotificationCampaignView"], Schemas["CreateNotificationCampaignRequest"]>("/api/v1/admin/notifications/campaigns", body, { signal, cache: "no-store" })).data;
}
